'use client';

import styled from '@emotion/styled';
import { floor } from 'libs/rtzr-commons/NumberUtils';
import Image from 'next/image';
import React, { PropsWithChildren, createContext, useCallback, useRef } from 'react';

interface IState {
  toast(message: string, onHideTooltip?: () => void, proBadge?: boolean): void;
}

export const GlobalToastMessageContext = createContext<IState>({
  toast: () => new Error(),
});

export const GlobalToastMessageProvider: React.FC<PropsWithChildren> = (props) => {
  const timeoutRef = useRef<number>();
  const tooltipRef = useRef<HTMLDivElement>();
  const tooltipMessageRef = useRef<HTMLSpanElement>();
  const proBadgeRef = useRef<HTMLImageElement>(null);

  const hideTooltip = useCallback(() => {
    if (!tooltipRef.current) return;
    tooltipRef.current.style.opacity = '0';
  }, []);

  const showTooltip = useCallback(
    (message: string, onCloseTooltip?: () => void, proBadge?: boolean) => {
      if (proBadgeRef.current) {
        if (proBadge) {
          proBadgeRef.current.style.display = 'inline-block';
        } else {
          proBadgeRef.current.style.display = 'none';
        }
      }
      if (!tooltipMessageRef.current || !tooltipRef) return;
      tooltipMessageRef.current.textContent = message;
      tooltipRef.current.style.opacity = '1';
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = self.setTimeout(() => {
        hideTooltip();
        onCloseTooltip?.();
      }, 2000);
    },
    []
  );

  return (
    <GlobalToastMessageContext.Provider value={{ toast: showTooltip }}>
      <GlobalTooltipRoot id="global-tooltip-root">
        <TooltipContainer ref={tooltipRef} transitionMs={300}>
          <Image
            ref={proBadgeRef}
            src="/callabo/common/pro_badge.svg"
            alt="Pro Badge"
            width={31}
            height={16}
            style={{
              display: 'none',
            }}
          />
          <span ref={tooltipMessageRef}></span>
        </TooltipContainer>
      </GlobalTooltipRoot>
      {props.children}
    </GlobalToastMessageContext.Provider>
  );
};

const GlobalTooltipRoot = styled.div`
  z-index: 9101;
  position: absolute;
  top: calc(100% - 168px);
  width: 100%;
  height: 168px;
  pointer-events: none;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const TooltipContainer = styled.div<{
  transitionMs: number;
}>`
  position: fixed;
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: 'center';
  max-width: 'calc(100% - 24px)';
  opacity: 0;
  transition: ${(props) =>
    props.transitionMs ? `opacity ${floor(props.transitionMs / 1000, 1)}s` : ''};
  padding: 14px 20px;

  background: #333333;
  border-radius: 4px;
  span {
    font-style: normal;
    font-weight: 400;
    font-size: 0.875rem;

    color: #ffffff;
    display: table-cell;

    flex: none;
    order: 0;
    flex-grow: 0;
    margin: auto;
    white-space: pre-line;
  }
`;
